import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconWebhook } from '@components/icons/webhook';
import { InfoBox } from '@components/info-box';
import { useIntegrationWebhook } from '@components/integrations';
import { RelativeSpinner } from '@components/loading-spinner';
import { Sidebar, hideSidebar } from '@components/sidebar';
import { SquareImage } from '@components/square-image';
import { useScopedTranslate } from '@locale/translate';

import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import {
  ActionHeader,
  SidebarProps,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';

import { WebhookConditionComponent, WebhookConditionData } from './common';

import { getFieldMappingId } from '../helpers';

export const SidebarContent = (props: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<'webhook'>(journeyActionId);
  const values = data?.values;
  const webhookId = data?.values?.integrationWebhookId.value ?? '';
  const editMutation = useUpdateJourneyAction(journeyActionId);
  const {
    data: webhookData,
    isLoading: endpointIsLoading,
    isError: webhooktIsError,
  } = useIntegrationWebhook(webhookId);
  const fieldMappingId = getFieldMappingId(data?.journey ?? '');

  const baseVariableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    types: ['email', 'text', 'select', 'multi_select', 'phone', 'date', 'separator', 'boolean'],
  });
  if (isLoading || endpointIsLoading || !data) {
    return <RelativeSpinner />;
  }

  const conditions = (webhookData?.conditions ?? []).filter((condition) => condition.visible);
  const conditionDefaultData = values?.conditionsData ?? {};

  const handleSubmit = async (data: WebhookConditionData, description: string) => {
    editMutation.mutateAsync({
      isAutomatic: true,
      action: 'webhook',
      description: description,
      values: {
        ...values,
        conditionsData: data,
      },
    });
  };

  return (
    <Sidebar>
      <ActionHeader
        title={webhookData?.name ?? t('title')}
        icon={IconWebhook}
        iconColor="green200"
        customIcon={
          webhookData?.thumbnail ? <SquareImage src={webhookData.thumbnail} /> : undefined
        }
        {...props}
      />
      <Sidebar.Content>
        {webhooktIsError ? (
          <InfoBox variant="red" icon={<IconFormIndicatorError />} text={t('webhookError')} />
        ) : (
          <WebhookConditionComponent
            integrationId={webhookData?.integration ?? ''}
            initialConditions={conditions}
            variableGroups={baseVariableGroups}
            defaultData={conditionDefaultData}
            onCancel={hideSidebar}
            onSubmit={handleSubmit}
            fieldMappingId={fieldMappingId}
            description={data.description}
          />
        )}
      </Sidebar.Content>
    </Sidebar>
  );
};
const sidebarLocale = {
  en: {
    title: 'Webhook',
    value: 'Value',
    actionDetails: 'Action details',
    conditionDescription: 'Continues when specific conditions are met',
    conditions: 'Conditions',
    webhookError:
      'Failed to load webhook, if the webhook has been deleted please replace the action in the journey builder',
  },
};
